import { useMemo } from 'react';
import { format } from 'date-fns';
import ReactApexChart from 'react-apexcharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface WalletGrowthDataPoint {
  date: string;
  activeWallets: number;
  totalWallets: number;
}

interface WalletGrowthChartProps {
  data: WalletGrowthDataPoint[];
  title?: string;
  height?: number;
}

/**
 * Area chart showing wallet growth over time (active vs total wallets)
 */
export function WalletGrowthChart({
  data,
  title = 'Wallet Growth',
  height = 350,
}: WalletGrowthChartProps) {
  const chartOptions = useMemo(() => {
    const dates = data.map((point) => format(new Date(point.date), 'MMM dd'));
    const activeWallets = data.map((point) => point.activeWallets);
    const totalWallets = data.map((point) => point.totalWallets);

    return {
      series: [
        {
          name: 'Active Wallets',
          data: activeWallets,
          color: '#10b981', // green-500
        },
        {
          name: 'Total Wallets',
          data: totalWallets,
          color: '#6366f1', // indigo-500
        },
      ],
      options: {
        chart: {
          type: 'area' as const,
          height,
          toolbar: {
            show: true,
            tools: {
              download: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: false,
              reset: true,
            },
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 2,
          curve: 'smooth' as const,
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.6,
            opacityTo: 0.1,
            stops: [0, 90, 100],
          },
        },
        xaxis: {
          categories: dates,
          title: {
            text: 'Date',
          },
          labels: {
            rotate: -45,
            rotateAlways: false,
          },
        },
        yaxis: {
          title: {
            text: 'Number of Wallets',
          },
          labels: {
            formatter: (value: number) => Math.floor(value).toLocaleString(),
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value: number) =>
              Math.floor(value).toLocaleString() + ' wallets',
          },
        },
        legend: {
          position: 'top' as const,
          horizontalAlign: 'right' as const,
        },
        grid: {
          borderColor: '#e5e7eb',
          strokeDashArray: 4,
        },
      },
    };
  }, [data, height]);

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ReactApexChart
          options={chartOptions.options}
          series={chartOptions.series}
          type="area"
          height={height}
        />
      </CardContent>
    </Card>
  );
}
